import { useEffect, useRef } from "react";

/**
 * Map component:
 * - If start has valid lat/lng → show start marker at that point.
 * - If end has valid lat/lng → show end marker at that point.
 * - Draw the route (polyline) only if historico is an array with more than 2 elements.
 *
 * @param {Object} start - { lat, lng }
 * @param {Object} end - { lat, lng }
 * @param {Array} historico - array of { lat, lng, ... }; route is drawn only when length > 2
 */
export default function MapaInicioFin({ start, end, historico = [], tag }) {
    const mapRef = useRef(null);
    const polylineRef = useRef(null);
    const markersRef = useRef([]);
    const labelOverlaysRef = useRef([]);

    const normalizeCoord = (coord) => {
        if (!coord || typeof coord !== "object") return null;
        const lat = parseFloat(coord.lat ?? coord.latitud);
        const lng = parseFloat(coord.lng ?? coord.longitud);
        if (Number.isNaN(lat) || Number.isNaN(lng)) return null;
        return { lat, lng };
    };

    const startCoord = normalizeCoord(start);
    const endCoord = normalizeCoord(end);
    const drawRoute = Array.isArray(historico) && historico.length > 2;
    const hasSomethingToShow = startCoord || endCoord || drawRoute;
    const height = 320;

    useEffect(() => {
        if (!window.google?.maps || !mapRef.current) return;
        if (!hasSomethingToShow) return;

        const pathPoints = drawRoute
            ? historico.map((p) => {
                const c = normalizeCoord(p);
                return c ? new window.google.maps.LatLng(c.lat, c.lng) : null;
            }).filter(Boolean)
            : [];

        function LabelBelowMarkerOverlay(position, text, map) {
            this.position = position;
            this.text = text;
            this.div = null;
            this.setMap(map);
        }
        LabelBelowMarkerOverlay.prototype = new window.google.maps.OverlayView();
        LabelBelowMarkerOverlay.prototype.onAdd = function () {
            const div = document.createElement("div");
            div.style.position = "absolute";
            div.style.whiteSpace = "nowrap";
            div.style.color = "#fff";
            div.style.fontWeight = "bold";
            div.style.fontSize = "12px";
            div.style.pointerEvents = "none";
            div.style.transform = "translate(-50%, 0)";
            div.style.background = "rgba(0,0,0,0.7)";
            div.style.padding = "2px 8px";
            div.style.borderRadius = "4px";
            div.textContent = this.text;
            this.div = div;
            const panes = this.getPanes();
            if (panes?.overlayMouseTarget) panes.overlayMouseTarget.appendChild(div);
        };
        LabelBelowMarkerOverlay.prototype.draw = function () {
            if (!this.div || !this.position) return;
            const projection = this.getProjection();
            if (!projection) return;
            const point = projection.fromLatLngToDivPixel(this.position);
            if (!point) return;
            const height = 8;
            this.div.style.left = point.x + "px";
            this.div.style.top = point.y + height + "px";
        };
        LabelBelowMarkerOverlay.prototype.onRemove = function () {
            if (this.div?.parentNode) this.div.parentNode.removeChild(this.div);
            this.div = null;
        };

        const bounds = new window.google.maps.LatLngBounds();
        if (pathPoints.length >= 2) {
            pathPoints.forEach((pt) => bounds.extend(pt));
        }
        if (startCoord) bounds.extend(new window.google.maps.LatLng(startCoord.lat, startCoord.lng));
        if (endCoord) bounds.extend(new window.google.maps.LatLng(endCoord.lat, endCoord.lng));
        const isEmpty = bounds.isEmpty();
        const defaultCenter = { lat: -17.8, lng: -63.2 };

        const map = new window.google.maps.Map(mapRef.current, {
            center: isEmpty ? defaultCenter : bounds.getCenter(),
            zoom: 12,
            mapTypeControl: true,
            streetViewControl: false,
            fullscreenControl: true,
            zoomControl: true,
        });

        if (!isEmpty) map.fitBounds(bounds, 48);

        if (pathPoints.length >= 2) {
            const polyline = new window.google.maps.Polyline({
                path: pathPoints,
                strokeColor: "#2563eb",
                strokeOpacity: 1,
                strokeWeight: 4,
                map,
            });
            polylineRef.current = polyline;
        }

        const startLabel = typeof tag === "function" ? tag("label_inicio") : "Inicio";
        const endLabel = typeof tag === "function" ? tag("label_fin") : "Fin";
        const markers = [];
        const labelOverlays = [];

        if (startCoord) {
            const startLatLng = new window.google.maps.LatLng(startCoord.lat, startCoord.lng);
            markers.push(new window.google.maps.Marker({ map, position: startLatLng, title: startLabel }));
            labelOverlays.push(new LabelBelowMarkerOverlay(startLatLng, startLabel, map));
        }
        if (endCoord) {
            const endLatLng = new window.google.maps.LatLng(endCoord.lat, endCoord.lng);
            markers.push(new window.google.maps.Marker({ map, position: endLatLng, title: endLabel }));
            labelOverlays.push(new LabelBelowMarkerOverlay(endLatLng, endLabel, map));
        }

        markersRef.current = markers;
        labelOverlaysRef.current = labelOverlays;

        return () => {
            labelOverlaysRef.current.forEach((o) => o?.setMap?.(null));
            labelOverlaysRef.current = [];
            if (polylineRef.current) polylineRef.current.setMap(null);
            markersRef.current.forEach((m) => m?.setMap?.(null));
        };
    }, [
        hasSomethingToShow,
        drawRoute,
        historico,
        start?.lat, start?.lng, start?.latitud, start?.longitud,
        end?.lat, end?.lng, end?.latitud, end?.longitud,
    ]);

    if (!hasSomethingToShow) {
        return (
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-500" style={{ height: typeof height === "number" ? `${height}px` : undefined }}>
                Faltan coordenadas de inicio o fin.
            </div>
        );
    }

    return (
        <div
            ref={mapRef}
            className="w-full rounded-lg border border-gray-200 overflow-hidden bg-gray-100"
            style={{ height: typeof height === "number" ? `${height}px` : height }}
        />
    );
}
